import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import type { LessonLog, Student, TestRecord } from '@/types';
import LessonCardItem from './components/LessonCardItem';
import { markProgressPlanned } from '@/lib/progress';

export default function LessonsPage() {
  const [lessons, setLessons] = useState<LessonLog[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [testsByLesson, setTestsByLesson] = useState<Record<string, TestRecord[]>>({});
  const [progressMap, setProgressMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [studentFilter, setStudentFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [studentsRes, lessonsRes] = await Promise.all([
        supabase.from('students').select('*').order('name', { ascending: true }),
        supabase.from('lesson_logs').select('*').order('lesson_date', { ascending: false }),
      ]);
      if (studentsRes.error) throw studentsRes.error;
      if (lessonsRes.error) throw lessonsRes.error;

      const st = (studentsRes.data as Student[]) ?? [];
      const ls = (lessonsRes.data as LessonLog[]) ?? [];
      setStudents(st);
      setLessons(ls);

      const ids = ls.map((l) => l.id);
      if (ids.length > 0) {
        const { data: tr, error: testError } = await supabase
          .from('test_records')
          .select('*')
          .in('lesson_log_id', ids);
        if (testError) throw testError;
        const map: Record<string, TestRecord[]> = {};
        ((tr as TestRecord[]) ?? []).forEach((t) => {
          if (!t.lesson_log_id) return;
          if (!map[t.lesson_log_id]) map[t.lesson_log_id] = [];
          map[t.lesson_log_id].push(t);
        });
        setTestsByLesson(map);
      } else {
        setTestsByLesson({});
      }

      const linkedIds = Array.from(new Set(ls.flatMap((l) => l.progress_item_ids ?? [])));
      if (linkedIds.length > 0) {
        const { data: pi } = await supabase
          .from('progress_items')
          .select('id, unit')
          .in('id', linkedIds);
        const pmap: Record<string, string> = {};
        ((pi as { id: string; unit: string }[]) ?? []).forEach((p) => {
          pmap[p.id] = p.unit;
        });
        setProgressMap(pmap);
      } else {
        setProgressMap({});
      }
    } catch {
      setError('수업일지를 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const nameMap = useMemo(() => {
    const map = new Map<string, string>();
    students.forEach((s) => map.set(s.id, s.name));
    return map;
  }, [students]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return lessons.filter((l) => {
      if (studentFilter !== 'all' && l.student_id !== studentFilter) return false;
      if (!q) return true;
      const name = (nameMap.get(l.student_id) ?? '').toLowerCase();
      return (
        name.includes(q) ||
        (l.lesson_scope ?? '').toLowerCase().includes(q) ||
        (l.next_homework ?? '').toLowerCase().includes(q)
      );
    });
  }, [lessons, nameMap, query, studentFilter]);

  const remove = async (id: string) => {
    try {
      const target = lessons.find((l) => l.id === id);
      await supabase.from('test_records').delete().eq('lesson_log_id', id);
      const { error: deleteError } = await supabase.from('lesson_logs').delete().eq('id', id);
      if (deleteError) throw deleteError;
      await markProgressPlanned(target?.progress_item_ids ?? []);
      setLessons((prev) => prev.filter((l) => l.id !== id));
    } catch {
      setError('삭제에 실패했어요.');
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground-950">수업일지</h1>
          <p className="mt-1.5 text-sm text-foreground-600">
            매 수업의 범위와 숙제, 테스트 기록을 남기고 한눈에 확인해요.
          </p>
        </div>
        <Link
          to="/lessons/new"
          className="flex items-center gap-1.5 self-start rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          수업일지 작성
        </Link>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative sm:w-64">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="학생 · 수업 범위 · 숙제로 검색"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <select
            value={studentFilter}
            onChange={(e) => setStudentFilter(e.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400 sm:w-48"
          >
            <option value="all">전체 학생</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <p className="text-sm text-foreground-500">
          전체 <strong className="font-semibold text-foreground-800">{visible.length}</strong>건
        </p>
      </div>

      {error && !loading && (
        <div className="mt-6 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-100/70 px-4 py-2.5 text-sm text-accent-800">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={load}
            className="rounded-md border border-accent-300 px-3 py-1 text-xs font-semibold text-accent-800 hover:bg-accent-100 whitespace-nowrap"
          >
            다시 시도
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-foreground-500">
          <i className="ri-loader-4-line mr-2 animate-spin text-xl"></i>
          불러오는 중...
        </div>
      ) : visible.length === 0 ? (
        <div className="mt-6 flex flex-col items-center gap-3 rounded-xl border border-dashed border-background-300 bg-background-100 py-20">
          <i className="ri-book-2-line text-3xl text-foreground-400"></i>
          <p className="text-sm text-foreground-600">
            {query || studentFilter !== 'all' ? '조건에 맞는 수업일지가 없어요.' : '아직 기록된 수업일지가 없어요.'}
          </p>
          {!query && studentFilter === 'all' && (
            <Link
              to="/lessons/new"
              className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              첫 수업일지 작성하기
            </Link>
          )}
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-3">
          {visible.map((l) => (
            <LessonCardItem
              key={l.id}
              lesson={l}
              studentName={nameMap.get(l.student_id)}
              tests={testsByLesson[l.id] ?? []}
              progressUnits={(l.progress_item_ids ?? [])
                .map((pid) => progressMap[pid])
                .filter(Boolean)}
              to={`/lessons/${l.id}/edit`}
              onDelete={remove}
            />
          ))}
        </div>
      )}
    </div>
  );
}